import React from 'react';
import styled from 'styled-components/macro';
import {
	Button,
	FlexColumn,
	FlexRow,
	Icon,
	Modal,
	Pill,
	PillVariant,
	Text,
	ExpandableContainer,
	Frame,
} from '@forecast-it/design-system';
import {MODAL_TYPE, showModal} from '../../forecast-app/shared/components/modals/generic_modal_conductor';
import RichTextItem from '../richtext_item';
import {useIntl} from 'react-intl';
import Util from '../../forecast-app/shared/util/util';
import {hasFeatureFlag} from '../../forecast-app/shared/util/FeatureUtil';
import {BUDGET_TYPE} from '../../constants';
import {DeliverableEdges, Deliverable, DeliverableEstimateRef} from './BaselineTypes';

type ManageDeliverablesModalProps = {
	closeModal: () => void;
	deliverables: DeliverableEdges;
	projectId: string;
	/** Which Deliverables have Baseline estimates; `undefined` when the opener hasn't loaded them. */
	baselineItems?: DeliverableEstimateRef[];
	budgetType?: string;
	/** The project's currency: its rate card's, falling back to the company's. */
	currency?: string;
};

export const RichTextWrapper = styled.div`
	overflow: hidden;
	.editor {
		padding: 8px 0 0 0;
		margin: 0;
		.public-DraftEditor-content {
			min-height: 0px;
		}
	}
	.DraftEditor-editorContainer,
	.DraftEditor-root {
		position: unset;
		border-left-width: 0;
	}
`;

const ManageDeliverablesModal = ({
	closeModal,
	deliverables,
	projectId,
	baselineItems,
	budgetType,
	currency,
}: ManageDeliverablesModalProps): JSX.Element => {
	const intl = useIntl();

	// A price bills a Deliverable as Fixed Price only on a Time & Materials project; the other
	// budget types' calculations ignore it. So the badge appears exactly where the editor offers
	// the Fixed Price switch, and never claims a billing type that is not in effect.
	const showsFixedPrice = hasFeatureFlag('fixed_fee_deliverables_on_t&m') && budgetType === BUDGET_TYPE.TIME_AND_MATERIALS;

	const formatPrice = (price: number) =>
		currency
			? intl.formatNumber(price, {style: 'currency', currency, currencyDisplay: 'narrowSymbol'})
			: intl.formatNumber(price, {minimumFractionDigits: 2, maximumFractionDigits: 2});

	// A zero fee is still a fee, and 0 is falsy — hence the explicit null check.
	const fixedPriceBadge = (deliverable?: Deliverable | null) =>
		showsFixedPrice && deliverable?.price != null ? (
			<Pill variant={PillVariant.INFORMATION}>
				{intl.formatMessage(
					{id: 'deliverables.manage.modal.fixed_price_badge'},
					{price: formatPrice(deliverable.price)}
				)}
			</Pill>
		) : undefined;

	const onEditClick = (deliverable: Deliverable) => {
		closeModal();
		showModal({
			type: MODAL_TYPE.DELIVERABLE_EDITOR_MODAL,
			deliverables,
			deliverable,
			action: 'edit',
			projectId,
			baselineItems,
			budgetType,
			currency,
		});
	};

	const onCreateClick = () => {
		closeModal();
		showModal({
			type: MODAL_TYPE.DELIVERABLE_EDITOR_MODAL,
			deliverables,
			action: 'create',
			projectId,
			baselineItems,
			budgetType,
			currency,
		});
	};

	return (
		<Modal size={Modal.SIZE.EXTRA_LARGE} onCloseModal={closeModal} setHeight={true}>
			<Modal.Title>{intl.formatMessage({id: 'deliverables.guide_section.deliverables.button'})} </Modal.Title>
			<Modal.Content>
				<Button emphasis={'medium'} onPress={onCreateClick} data-cy="add-new-deliverable">
					{intl.formatMessage({id: 'deliverables.manage.modal.add_new_deliverable'})}
				</Button>
				<FlexColumn>
					<FlexRow>
						<Icon icon="deliverable"></Icon>
						<Text>{`Deliverables (${deliverables.length})`}</Text>
					</FlexRow>
					{deliverables.length === 0 ? (
						<Frame height={'s'} centerContent={true}>
							<Text size={'2'}>{intl.formatMessage({id: 'deliverables.manage.modal.no_deliverables'})}</Text>
						</Frame>
					) : (
						<FlexColumn>
							{deliverables.map(deliverable => (
								<ExpandableContainer
									primaryText={deliverable?.node?.name || ''}
									secondaryText={
										deliverable?.node?.description
											? Util.ConvertDraftJsToPlainText(deliverable?.node?.description)
											: ''
									}
									badge={fixedPriceBadge(deliverable?.node)}
									onActionClick={() => deliverable?.node && onEditClick(deliverable?.node)}
									isExpandable={true}
								>
									<RichTextWrapper>
										<RichTextItem
											alwaysShowControls={false}
											readOnly={true}
											text={deliverable?.node?.description}
										/>
									</RichTextWrapper>
								</ExpandableContainer>
							))}
						</FlexColumn>
					)}
				</FlexColumn>
			</Modal.Content>
			{deliverables.length > 0 && (
				<Modal.Footer>
					{/** Hack to enable scrollbar if content is too long */}
					<></>
				</Modal.Footer>
			)}
		</Modal>
	);
};

export default ManageDeliverablesModal;
